import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import LudoTermsDialog from '../components/LudoTermsDialog';
import LudoOnlinePanel from '../../ludo/LudoOnlinePanel.jsx';
import { DASHBOARD_MAX_LUDO_PLAYERS } from '../../ludo/ludoConstants.js';
import { useLudoPresence } from '../../ludo/useLudoPresence.js';
import jetxpressImg from '../../../assets/games/jetxpress.jpg';
import luckydrawImg from '../../../assets/games/LuckyDraw.png';
import wingoImg from '../../../assets/games/Wingo.png';
import minesImg from '../../../assets/games/MinesDashboard.png';
import plinkoImg from '../../../assets/games/Plinko.png';
import limboImg from '../../../assets/games/Limbo.png';
import ludoImg from '../../../assets/games/LudoKing.png';
import elevenLogo from '../../../assets/games/ElevenLogo.png';
import lotteryIcon from '../../../assets/games/lottery.png';
import jackpotIcon from '../../../assets/games/jackpot.png';
import originalIcon from '../../../assets/games/Original.png';
import './DashboardScreen.css';

const LUDO_GAME = {
  id: 'ludo',
  title: 'Ludo',
  subtitle: 'Win real money — roll dice, beat your opponent & take the pool',
  tag: 'PVP',
  tagClass: 'tag-classic',
  image: ludoImg,
  cardClass: 'game-card--ludo',
  stats: ['2-player only', 'Live stakes', 'Play with friends'],
};

const CATEGORY_NAV = [
  { id: 'lottery', label: 'Lottery', icon: lotteryIcon },
  { id: 'jackpot', label: 'Super Jackpot', icon: jackpotIcon },
  { id: 'original', label: 'Original', icon: originalIcon },
];

const GAME_SECTIONS = [
  {
    id: 'lottery',
    title: 'Lottery',
    icon: lotteryIcon,
    subtitle: 'Fair and diverse lottery gameplay',
    layout: 'wide',
    games: [
      { id: 'wingo', title: 'Win Go', image: wingoImg, cardClass: 'game-card--wingo' },
      {
        id: 'luckydraw',
        title: 'Lucky Draw',
        image: luckydrawImg,
        cardClass: 'game-card--luckydraw',
        themedBg: true,
      },
    ],
  },
  {
    id: 'jackpot',
    title: 'Super Jackpot',
    subtitle: 'High multipliers — time your cashout for massive wins',
    layout: 'compact',
    games: [
      { id: 'mines', title: 'Mines', image: minesImg, cardClass: 'game-card--mines' },
      { id: 'limbo', title: 'Limbo', image: limboImg, cardClass: 'game-card--limbo' },
      { id: 'fairaviator', title: 'jetXpress', image: jetxpressImg, cardClass: 'game-card--fairaviator' },
    ],
  },
  {
    id: 'original',
    title: 'Original',
    subtitle: 'Classic arcade originals — fast rounds, instant fun',
    layout: 'compact',
    games: [
      { id: 'plinko', title: 'Plinko', image: plinkoImg, cardClass: 'game-card--plinko' },
      { id: 'mines', title: 'Mines', image: minesImg, cardClass: 'game-card--mines' },
    ],
  },
];

const TOTAL_GAMES = 1 + GAME_SECTIONS.reduce((n, s) => n + s.games.length, 0);

function GameSection({ section, onOpenGame }) {
  const rowRef = useRef(null);

  const scrollRow = (direction) => {
    rowRef.current?.scrollBy({ left: direction * 180, behavior: 'smooth' });
  };

  const isWide = section.layout === 'wide';

  return (
    <Box className={`dash-section dash-section--${section.id}`} id={`section-${section.id}`}>
      {section.icon ? (
        <Box className="dash-section__icon-banner">
          <img src={section.icon} alt="" className="dash-section__icon-banner-img" />
        </Box>
      ) : null}
      <Box className="dash-section__header">
        <Box className="dash-section__title-row">
          <span className="dash-section__bar" aria-hidden />
          <Typography className="dash-section__title">{section.title}</Typography>
          <span className="dash-section__count">More {section.games.length}</span>
        </Box>
        <Box className="dash-section__nav">
          <IconButton
            size="small"
            className="dash-section__arrow"
            onClick={() => scrollRow(-1)}
            aria-label={`Scroll ${section.title} left`}
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            className="dash-section__arrow"
            onClick={() => scrollRow(1)}
            aria-label={`Scroll ${section.title} right`}
          >
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Typography className="dash-section__subtitle">{section.subtitle}</Typography>

      <Box
        ref={rowRef}
        className={`dash-section__row${isWide ? ' dash-section__row--wide' : ''}`}
      >
        {section.games.map((game) => (
          <button
            key={game.id}
            type="button"
            className={`game-card game-card--image-only game-card--section${isWide ? ' game-card--wide' : ' game-card--compact-tile'} ${game.cardClass}${game.themedBg ? ' game-card--themed-lottery game-card--with-title' : ''}`}
            onClick={() => onOpenGame(game.id)}
          >
            {game.themedBg ? (
              <span className="game-card__label">{game.title}</span>
            ) : null}
            <img src={game.image} alt={game.title} className="game-card__cover" />
          </button>
        ))}
      </Box>
    </Box>
  );
}

function DashboardScreen({
  user,
  onOpenFairAviator,
  onOpenAviator,
  onOpenWinGo,
  onOpenLuckyDraw,
  onOpenMatka,
  onOpenMines,
  onOpenPlinko,
  onOpenLimbo,
  onOpenLudo,
  onLudoMatchFound,
  onOpenAccount,
}) {
  const [nav, setNav] = useState(0);
  const [ludoTermsOpen, setLudoTermsOpen] = useState(false);
  const [ludoInviteStake, setLudoInviteStake] = useState(100);
  const [toast, setToast] = useState('');
  const scrollRef = useRef(null);
  const wallet = user?.walletBalance ?? 0;

  const {
    online: ludoOnline,
    pendingInvites: ludoPendingInvites,
    loading: ludoPresenceLoading,
    refreshing: ludoRefreshing,
    refresh: refreshLudoPresence,
    sendInvite: sendLudoInvite,
    respondInvite: respondLudoInvite,
  } = useLudoPresence({
    enabled: true,
    joinLobby: false,
    autoResume: false,
    availableStake: null,
    onMatchFound: onLudoMatchFound,
    onInviteReceived: (inv) => {
      setToast(`${inv?.from?.name || 'Someone'} invited you to Ludo · ₹${inv?.stake}`);
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, []);

  const openGame = (id) => {
    if (id === 'fairaviator') onOpenFairAviator?.();
    if (id === 'aviator') onOpenAviator?.();
    if (id === 'wingo') onOpenWinGo?.();
    if (id === 'luckydraw') onOpenLuckyDraw?.();
    if (id === 'matka') onOpenMatka?.();
    if (id === 'mines') onOpenMines?.();
    if (id === 'plinko') onOpenPlinko?.();
    if (id === 'limbo') onOpenLimbo?.();
    if (id === 'ludo') onOpenLudo?.();
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(`section-${sectionId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleNav = (_, value) => {
    setNav(value);
    if (value === 1) onOpenAccount?.();
    else setNav(0);
  };

  return (
    <Box className="home-root home-root--gaming">
      <header className="home-header">
        <Box className="home-header__top">
          <img src={elevenLogo} alt="Eleven Games" className="home-brand-logo" style={{
    width: '220px',
    height: '90px',
    objectFit: 'contain',
  }}/>
          <Box className="home-wallet" onClick={onOpenAccount} role="button" tabIndex={0}>
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: '#4ade80' }} />
            <Box className="home-wallet__text">
              <Typography component="span" className="home-wallet__label">Balance</Typography>
              <Typography component="span" className="home-wallet__amount">
                ₹{wallet.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </header>

      <Box className="home-scroll" ref={scrollRef}>
        {user?.depositBonusEligible ? (
          <Box className="home-bonus-ticker" aria-live="polite">
            <CampaignOutlinedIcon className="home-bonus-ticker__icon" />
            <Box className="home-bonus-ticker__viewport">
              <Box className="home-bonus-ticker__track">
                <Typography component="span" className="home-bonus-ticker__text">
                  {user.bonusesGivenCount === 0 ? (
                    <>
                      <strong>Deposit bonus:</strong> Get <strong>5% extra</strong> on your 1st deposit and{' '}
                      <strong>10% extra</strong> on your 2nd deposit!
                    </>
                  ) : (
                    <>
                      <strong>Last chance!</strong> Get <strong>10% extra bonus</strong> on your 2nd deposit.
                    </>
                  )}
                </Typography>
                <Typography component="span" className="home-bonus-ticker__text" aria-hidden="true">
                  {user.bonusesGivenCount === 0 ? (
                    <>
                      <strong>Deposit bonus:</strong> Get <strong>5% extra</strong> on your 1st deposit and{' '}
                      <strong>10% extra</strong> on your 2nd deposit!
                    </>
                  ) : (
                    <>
                      <strong>Last chance!</strong> Get <strong>10% extra bonus</strong> on your 2nd deposit.
                    </>
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}

        <LudoOnlinePanel
          compact
          showWhenEmpty
          maxPlayers={DASHBOARD_MAX_LUDO_PLAYERS}
          online={ludoOnline}
          pendingInvites={ludoPendingInvites}
          inviteStake={ludoInviteStake}
          onInviteStakeChange={setLudoInviteStake}
          onRefresh={refreshLudoPresence}
          refreshing={ludoRefreshing}
          onSendInvite={async (id, stake) => {
            const ok = await sendLudoInvite(id, stake);
            setToast(ok ? 'Ludo invite sent' : 'Could not send invite');
          }}
          onRespondInvite={async (id, accept) => {
            await respondLudoInvite(id, accept);
            setToast(accept ? 'Opening Ludo match…' : 'Invite declined');
          }}
          onOpenLudo={onOpenLudo}
          loading={ludoPresenceLoading}
        />

        <Box className="home-featured-header">
          <Typography className="home-featured-heading">Ludo Game</Typography>
          <Typography className="home-featured-sub">
            Roll dice, beat your opponent &amp; win real money
          </Typography>
        </Box>

        <Box className="home-featured-stack">
          <Box className={`game-card game-card--featured game-card--split ${LUDO_GAME.cardClass}`}>
            <button
              type="button"
              className="game-card__tap game-card__tap--split"
              onClick={() => openGame(LUDO_GAME.id)}
            >
              <Box
                className="game-card__hero"
                style={{ backgroundImage: `url(${LUDO_GAME.image})` }}
                role="img"
                aria-label={LUDO_GAME.title}
              />
              <Box className="game-card__body">
                <Box className="game-card__head">
                  <span className={`game-tag ${LUDO_GAME.tagClass}`}>{LUDO_GAME.tag}</span>
                </Box>
                <Typography className="game-card__title game-card__title--dark">{LUDO_GAME.title}</Typography>
                <Typography className="game-card__subtitle game-card__subtitle--dark">{LUDO_GAME.subtitle}</Typography>
                <Box className="game-card__stats">
                  {LUDO_GAME.stats.map((s) => (
                    <span key={s} className="game-stat-chip">{s}</span>
                  ))}
                </Box>
                <Typography className="game-card__terms-note" component="p">
                  T&amp;C: 7% fee on pool · 30s to move (3× auto-move, then forfeit) · 1.5 min max to roll
                  dice · leaving = forfeit
                </Typography>
                <span className="game-play-btn game-play-btn--green">Play Now →</span>
              </Box>
            </button>
            <button
              type="button"
              className="game-card__terms-link game-card__terms-link--green"
              onClick={() => setLudoTermsOpen(true)}
            >
              Terms &amp; Conditions
            </button>
          </Box>
        </Box>

        <Box className="dash-categories">
          {CATEGORY_NAV.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`dash-category dash-category--${cat.id}`}
              onClick={() => scrollToSection(cat.id)}
            >
              <Box className={`dash-category__icon-wrap dash-category__icon-wrap--${cat.id}`}>
                <img src={cat.icon} alt="" className="dash-category__icon" />
              </Box>
              <Typography className="dash-category__label">{cat.label}</Typography>
            </button>
          ))}
        </Box>

        {GAME_SECTIONS.map((section) => (
          <GameSection key={section.id} section={section} onOpenGame={openGame} />
        ))}

        <LudoTermsDialog open={ludoTermsOpen} onClose={() => setLudoTermsOpen(false)} />

        <Box className="home-footer-note">
          <Typography component="p">Tap any game to start playing instantly</Typography>
        </Box>
      </Box>

      <Snackbar open={Boolean(toast)} message={toast} autoHideDuration={3500} onClose={() => setToast('')} />

      <nav className="home-bottom-nav">
        <BottomNavigation showLabels value={nav} onChange={handleNav}>
          <BottomNavigationAction label="Games" icon={<HomeOutlinedIcon />} />
          <BottomNavigationAction label="Account" icon={<AccountCircleOutlinedIcon />} />
        </BottomNavigation>
      </nav>
    </Box>
  );
}

export default DashboardScreen;
